import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Users, TrendingUp, Target, Shield, Zap } from 'lucide-react'

interface TeamAnalysisProps {
  setIsLoading: (loading: boolean) => void
}

interface TeamPrediction {
  player_id: number
  player_name: string
  team: string
  position: string
  predicted_points: number
  confidence: number
  factors: {
    xg_contribution: number
    xa_contribution: number
    clean_sheet_probability: number
    fixture_difficulty: number
    form_factor: number
    minutes_risk: number
    bonus_potential: number
  }
}

const teams = [
  { id: 1, name: 'Arsenal', short: 'ARS', color: 'from-red-500 to-red-700' },
  { id: 2, name: 'Aston Villa', short: 'AVL', color: 'from-claret-500 to-claret-700' },
  { id: 3, name: 'Bournemouth', short: 'BOU', color: 'from-red-500 to-black' },
  { id: 4, name: 'Brentford', short: 'BRE', color: 'from-red-500 to-white' },
  { id: 5, name: 'Brighton', short: 'BHA', color: 'from-blue-500 to-white' },
  { id: 6, name: 'Burnley', short: 'BUR', color: 'from-claret-500 to-sky-500' },
  { id: 7, name: 'Chelsea', short: 'CHE', color: 'from-blue-500 to-blue-700' },
  { id: 8, name: 'Crystal Palace', short: 'CRY', color: 'from-red-500 to-blue-500' },
  { id: 9, name: 'Everton', short: 'EVE', color: 'from-blue-500 to-blue-700' },
  { id: 10, name: 'Fulham', short: 'FUL', color: 'from-white to-black' },
  { id: 11, name: 'Liverpool', short: 'LIV', color: 'from-red-500 to-red-700' },
  { id: 12, name: 'Luton', short: 'LUT', color: 'from-orange-500 to-orange-700' },
  { id: 13, name: 'Manchester City', short: 'MCI', color: 'from-sky-500 to-sky-700' },
  { id: 14, name: 'Manchester United', short: 'MUN', color: 'from-red-500 to-red-700' },
  { id: 15, name: 'Newcastle', short: 'NEW', color: 'from-black to-white' },
  { id: 16, name: 'Nottingham Forest', short: 'NFO', color: 'from-red-500 to-white' },
  { id: 17, name: 'Sheffield United', short: 'SHU', color: 'from-red-500 to-white' },
  { id: 18, name: 'Tottenham', short: 'TOT', color: 'from-white to-navy-500' },
  { id: 19, name: 'West Ham', short: 'WHU', color: 'from-claret-500 to-claret-700' },
  { id: 20, name: 'Wolves', short: 'WOL', color: 'from-orange-500 to-black' },
]

const TeamAnalysis = ({ setIsLoading }: TeamAnalysisProps) => {
  const [selectedTeam, setSelectedTeam] = useState<number | null>(null)
  const [teamPredictions, setTeamPredictions] = useState<TeamPrediction[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchTeamPredictions = async (teamId: number) => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await fetch(`https://fpl-predicted-points.siward.workers.dev/predictions/team/${teamId}`)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      setTeamPredictions(data)
    } catch (err) {
      console.error('Failed to fetch team predictions:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch team predictions')
      setTeamPredictions([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setIsLoading(loading)
  }, [loading, setIsLoading])

  const handleTeamSelect = (teamId: number) => {
    setSelectedTeam(teamId)
    fetchTeamPredictions(teamId)
  }

  const getPositionColor = (position: string) => {
    switch (position) {
      case 'GK': return 'from-yellow-400 to-yellow-600'
      case 'DEF': return 'from-blue-400 to-blue-600'
      case 'MID': return 'from-green-400 to-green-600'
      case 'FWD': return 'from-red-400 to-red-600'
      default: return 'from-gray-400 to-gray-600'
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  }

  return (
    <section id="team-analysis" className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            <span className="gradient-text">Team Analysis</span>
          </h2>
          <p className="text-xl text-fpl-white/80 max-w-2xl mx-auto">
            Analyze predicted performance for specific teams and identify the best players for your FPL squad
          </p>
        </motion.div>

        {/* Team Selection Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12"
        >
          <h3 className="text-xl font-semibold mb-6 text-center text-fpl-white">Select a Team</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {teams.map((team, index) => (
              <motion.button
                key={team.id}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleTeamSelect(team.id)}
                className={`p-4 rounded-xl text-center transition-all duration-200 ${
                  selectedTeam === team.id
                    ? 'ring-2 ring-fpl-green shadow-lg'
                    : 'hover:shadow-md'
                }`}
                style={{
                  background: selectedTeam === team.id 
                    ? 'linear-gradient(135deg, #00ff85 0%, #37003c 100%)'
                    : 'rgba(42, 42, 42, 0.8)'
                }}
              >
                <div className="w-12 h-12 bg-gradient-to-br from-white/20 to-white/10 rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="text-lg font-bold text-white">{team.short}</span>
                </div>
                <div className="text-sm font-medium text-white">{team.name}</div>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Team Analysis Results */}
        <AnimatePresence>
          {selectedTeam && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              {/* Team Header */}
              <div className="text-center">
                <h3 className="text-2xl font-bold text-fpl-white mb-2">
                  {teams.find(t => t.id === selectedTeam)?.name} Predictions
                </h3>
                <p className="text-fpl-white/70">
                  {teamPredictions.length} players analyzed for upcoming fixtures
                </p>
              </div>

              {/* Team Stats Overview */}
              {teamPredictions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8"
                >
                  <div className="glass-effect p-4 rounded-xl text-center">
                    <div className="text-2xl font-bold text-fpl-green">
                      {teamPredictions.length}
                    </div>
                    <div className="text-sm text-fpl-white/70">Players</div>
                  </div>
                  <div className="glass-effect p-4 rounded-xl text-center">
                    <div className="text-2xl font-bold text-fpl-green">
                      {(teamPredictions.reduce((sum, p) => sum + p.predicted_points, 0) / teamPredictions.length).toFixed(1)}
                    </div>
                    <div className="text-sm text-fpl-white/70">Avg Points</div>
                  </div>
                  <div className="glass-effect p-4 rounded-xl text-center">
                    <div className="text-2xl font-bold text-fpl-green">
                      {teamPredictions.filter(p => p.predicted_points > 6).length}
                    </div>
                    <div className="text-sm text-fpl-white/70">High Potential</div>
                  </div>
                  <div className="glass-effect p-4 rounded-xl text-center">
                    <div className="text-2xl font-bold text-fpl-green">
                      {(teamPredictions.reduce((sum, p) => sum + p.confidence, 0) / teamPredictions.length * 100).toFixed(0)}%
                    </div>
                    <div className="text-sm text-fpl-white/70">Avg Confidence</div>
                  </div>
                </motion.div>
              )}

              {/* Player Predictions Grid */}
              {teamPredictions.length > 0 ? (
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                >
                  {teamPredictions
                    .sort((a, b) => b.predicted_points - a.predicted_points)
                    .map((player, index) => (
                    <motion.div
                      key={player.player_id}
                      variants={itemVariants}
                      whileHover={{ y: -5, scale: 1.02 }}
                      className="glass-effect p-6 rounded-xl card-hover"
                    >
                      {/* Player Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="text-lg font-semibold text-fpl-white">{player.player_name}</h4>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${getPositionColor(player.position)} text-white`}>
                              {player.position}
                            </span>
                            <span className="text-sm text-fpl-white/70">{player.team}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-fpl-green">{player.predicted_points.toFixed(1)}</div>
                          <div className="text-xs text-fpl-white/60">pts</div>
                        </div>
                      </div>

                      {/* Confidence Bar */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-fpl-white/70">Confidence</span>
                          <span className="text-fpl-green font-medium">{(player.confidence * 100).toFixed(0)}%</span>
                        </div>
                        <div className="w-full bg-fpl-light-gray rounded-full h-2">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${player.confidence * 100}%` }}
                            transition={{ duration: 1, delay: index * 0.1 }}
                            className="bg-gradient-to-r from-fpl-green to-fpl-blue h-2 rounded-full"
                          />
                        </div>
                      </div>

                      {/* Key Factors */}
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-fpl-white/70">xG + xA</span>
                          <span className="text-fpl-white">
                            {(player.factors.xg_contribution + player.factors.xa_contribution).toFixed(1)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-fpl-white/70">Form Factor</span>
                          <span className="text-fpl-white">{player.factors.form_factor}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-fpl-white/70">Fixture Difficulty</span>
                          <span className="text-fpl-white">{player.factors.fixture_difficulty}/5</span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-12"
                >
                  {loading ? (
                    <div className="text-fpl-white/70">Loading team predictions...</div>
                  ) : error ? (
                    <div className="text-fpl-red">
                      <div className="text-lg mb-2">Failed to load team predictions</div>
                      <p className="text-sm mb-4">{error}</p>
                      <button
                        onClick={() => fetchTeamPredictions(selectedTeam!)}
                        className="button-primary"
                      >
                        Try Again
                      </button>
                    </div>
                  ) : (
                    <div className="text-fpl-white/70">Select a team to view predictions</div>
                  )}
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  )
}

export default TeamAnalysis
